import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as AuthActions from './auth.actions';
import { AuthService } from '../data-access/auth.service';
import { AuthConfigService } from '../data-access/auth-config.service';
import { TokenStorageService } from '../data-access/token-storage.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      mergeMap(() => {
        return this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError((error) => {
            console.error('Error durante logout:', error);
            return of(AuthActions.logoutSuccess());
          }),
        );
      }),
    ),
  );
  checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkAuth),
      map(() => {
        if (this.authService.isAuthenticated()) {
          const user = this.authService.getUserValue();
          if (user) {
            return AuthActions.setUser({ user });
          }
        }
        return AuthActions.logoutSuccess();
      }),
    ),
  );
  private readonly translate = inject(TranslateService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly router = inject(Router);
  private readonly authConfig = inject(AuthConfigService);
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => {
          const postLoginRoute =
            this.authConfig.getConfig().postLoginRoute || this.authService.getFirstAvailableRoute();
          this.router.navigate([postLoginRoute]).then((r) => r);
        }),
      ),
    { dispatch: false },
  );
  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          console.log('🎯 AuthEffects - Navegando a login');
          this.router.navigate(['/login']).then((r) => r);
        }),
      ),
    { dispatch: false },
  );
  private readonly errorHandler = inject(ErrorHandler);
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ credentials }) => {
        return this.authService.login(credentials.email, credentials.password).pipe(
          map((loginSuccess: boolean) => {
            if (loginSuccess) {
              const user = this.authService.getUserValue();
              const token = this.authService.getToken();
              const refreshToken = this.tokenStorage.getRefreshToken();

              if (user && token) {
                return AuthActions.loginSuccess({
                  user,
                  token,
                  refreshToken: refreshToken || undefined,
                });
              } else {
                return AuthActions.loginFailure({
                  error: this.translate.instant('auth.errors.loginDataError'),
                });
              }
            } else {
              return AuthActions.loginFailure({
                error: this.translate.instant('auth.errors.invalidCredentials'),
              });
            }
          }),
          catchError((error) => {
            this.errorHandler.handleError(error);

            let errorMessage = this.translate.instant('auth.errors.loginError');

            if (error instanceof HttpErrorResponse) {
              if (error.status === 401) {
                errorMessage = this.translate.instant('auth.errors.unauthorized');
              } else if (error.status === 403) {
                errorMessage = this.translate.instant('auth.errors.forbidden');
              } else if (error.status >= 500) {
                errorMessage = this.translate.instant('auth.errors.serverError');
              } else if (error && typeof error === 'object' && 'userMessage' in error) {
                errorMessage = error.userMessage as string;
              } else if (error && typeof error === 'object' && 'message' in error) {
                errorMessage = String(error.message);
              }
            } else if (typeof error === 'string') {
              errorMessage = error;
            }

            return of(
              AuthActions.loginFailure({
                error: errorMessage,
              }),
            );
          }),
        );
      }),
    ),
  );
}
